import { useEffect, useState } from "react";
import { listCitasLazy } from "../api/odontologia";
import Loader from "../components/Loader";

// ──────────────────────────────────────────────
// Helpers de estilos / formato
// ──────────────────────────────────────────────
const ESTADO_MAP = {
  programada:  { label: "Programada",  cls: "bg-blue-50 text-blue-600 border-blue-200" },
  en_proceso:  { label: "En Proceso",  cls: "bg-amber-50 text-amber-600 border-amber-200" },
  "en proceso":{ label: "En Proceso",  cls: "bg-amber-50 text-amber-600 border-amber-200" },
  cancelada:   { label: "Cancelada",   cls: "bg-rose-50 text-rose-600 border-rose-200" },
  finalizada:  { label: "Finalizada",  cls: "bg-emerald-50 text-emerald-600 border-emerald-200" },
};

const TIPO_MAP = {
  procedimiento: { label: "Procedimiento", cls: "bg-indigo-50 text-indigo-600 border-indigo-200" },
  evaluacion:    { label: "Evaluación",    cls: "bg-violet-50 text-violet-600 border-violet-200" },
  consulta:      { label: "Consulta",      cls: "bg-sky-50 text-sky-600 border-sky-200" },
  control:       { label: "Control",       cls: "bg-teal-50 text-teal-600 border-teal-200" },
};

const estadoBadge = (estado) => {
  const key = estado?.toLowerCase();
  return ESTADO_MAP[key] || { label: estado || "—", cls: "bg-slate-50 text-slate-600 border-slate-200" };
};
const tipoBadge = (tipo) => {
  const key = tipo?.toLowerCase();
  return TIPO_MAP[key] || { label: tipo || "—", cls: "bg-slate-50 text-slate-600 border-slate-200" };
};

const formatHora = (t) => (t ? t.slice(0, 5) : "—");
const formatFecha = (f) => {
  if (!f) return "—";
  const [y, m, d] = f.split("-");
  return `${d}/${m}/${y}`;
};

const safeParseJSON = (data) => {
  if (!data) return [];
  try {
    let parsed = typeof data === "string" ? JSON.parse(data) : data;
    if (typeof parsed === "string") parsed = JSON.parse(parsed);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

// ──────────────────────────────────────────────
// Sección de detalle con ícono SVG inline
// ──────────────────────────────────────────────
const DetailRow = ({ label, value, accent }) => (
  <div>
    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">{label}</p>
    <p className={`text-sm font-semibold ${accent || "text-slate-700"}`}>{value || <span className="text-slate-300 italic font-normal">—</span>}</p>
  </div>
);

// ──────────────────────────────────────────────
// Componente principal
// ──────────────────────────────────────────────
const ListadoCitasOdontologia = () => {
  const [loading, setLoading]       = useState(true);
  const [citas, setCitas]           = useState([]);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit]           = useState(10);
  const [buscar, setBuscar]         = useState("");

  // Modal de detalle
  const [detalle, setDetalle] = useState(null);

  const fetchData = async (p = 1) => {
    setLoading(true);
    try {
      const data = await listCitasLazy(p, limit, buscar || " ");
      if (data) {
        setCitas(data.data || []);
        setTotalPages(data.totalPages || 1);
        setPage(data.currentPage || p);
      }
    } catch (error) {
      console.error("Error al obtener citas", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line
  }, [page, limit, buscar]);

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto">
      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Listado de Citas</h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Consulte todas las citas odontológicas registradas
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mb-4 sm:mb-6 bg-white p-3 sm:p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Buscar por paciente, estado, tipo de cita..."
            value={buscar}
            onChange={(e) => { setBuscar(e.target.value); setPage(1); }}
            className="pl-10 pr-4 py-2 w-full border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-slate-50 focus:bg-white text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 font-medium whitespace-nowrap">Mostrar:</span>
          <select
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            className="border border-slate-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm"
          >
            {[10, 15, 25, 50, 100, 250, 500, 1000].map((num) => (
              <option key={num} value={num}>{num} registros</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 bg-white/70 flex justify-center items-center z-10">
            <Loader />
          </div>
        )}

        {/* Tabla — md+ */}
        <div className="hidden md:block overflow-x-auto custom-scrollbar">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-500 bg-slate-50 uppercase border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">Fecha</th>
                <th className="px-4 py-3 font-semibold">Hora</th>
                <th className="px-4 py-3 font-semibold">Paciente</th>
                <th className="px-4 py-3 font-semibold">Teléfono</th>
                <th className="px-4 py-3 font-semibold text-center">Tipo Cita</th>
                <th className="px-4 py-3 font-semibold text-center">Estado</th>
                <th className="px-4 py-3 font-semibold text-center sticky right-0 bg-slate-100 border-l border-slate-200 z-10 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {citas.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-400 text-sm">
                    No se encontraron citas
                  </td>
                </tr>
              )}
              {citas.map((c) => {
                const ei = estadoBadge(c.estado);
                const ti = tipoBadge(c.tipo_cita);
                return (
                  <tr key={c.id} className="hover:bg-indigo-50/50 transition-colors group">
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs">{c.id}</td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap font-medium">{formatFecha(c.fecha)}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {formatHora(c.hora_inicio)} – {formatHora(c.hora_fin)}
                    </td>
                    <td className="px-4 py-3 text-slate-700 font-medium whitespace-nowrap">
                      {c.ClientesReceptor?.nombre || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {c.ClientesReceptor?.telefono || "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${ti.cls}`}>
                        {ti.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${ei.cls}`}>
                        {ei.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center sticky right-0 bg-slate-100 group-hover:bg-indigo-50 border-l border-slate-200 z-0 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.03)] transition-colors">
                      <button
                        onClick={() => setDetalle(c)}
                        className="bg-white border border-slate-200 text-slate-600 px-3 py-1.5 rounded-lg hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all text-xs font-medium"
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Cards — móvil */}
        <div className="md:hidden divide-y divide-slate-100">
          {citas.length === 0 && !loading && (
            <p className="p-6 text-center text-slate-400 text-sm">No se encontraron citas</p>
          )}
          {citas.map((c) => {
            const ei = estadoBadge(c.estado);
            const ti = tipoBadge(c.tipo_cita);
            return (
              <div key={c.id} className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-bold text-slate-800 text-sm">{c.ClientesReceptor?.nombre || "Paciente desconocido"}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {formatFecha(c.fecha)} · {formatHora(c.hora_inicio)} – {formatHora(c.hora_fin)}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${ei.cls}`}>
                    {ei.label}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${ti.cls}`}>{ti.label}</span>
                  <button
                    onClick={() => setDetalle(c)}
                    className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg text-xs font-semibold hover:bg-indigo-700 transition-colors"
                  >
                    Ver detalle
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Paginación */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-4 sm:mt-6 px-1 gap-3">
        <p className="text-sm text-slate-500 text-center sm:text-left">
          Página <span className="font-medium">{page}</span> de <span className="font-medium">{totalPages}</span>
        </p>
        <div className="flex gap-1 bg-white p-1 rounded-lg border border-slate-200 justify-center overflow-x-auto">
          <button
            disabled={page === 1}
            onClick={() => fetchData(page - 1)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${page === 1 ? "text-slate-400 cursor-not-allowed" : "text-slate-600 hover:bg-slate-100"}`}
          >
            ← Ant
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => {
            if (totalPages > 7) {
              if (num === 1 || num === totalPages || (num >= page - 1 && num <= page + 1)) {
                return (
                  <button key={num} onClick={() => fetchData(num)}
                    className={`min-w-[32px] px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${page === num ? "bg-indigo-50 text-indigo-600" : "text-slate-600 hover:bg-slate-100"}`}
                  >{num}</button>
                );
              } else if (num === page - 2 || num === page + 2) {
                return <span key={num} className="px-2 py-1.5 text-slate-400">...</span>;
              }
              return null;
            }
            return (
              <button key={num} onClick={() => fetchData(num)}
                className={`min-w-[32px] px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${page === num ? "bg-indigo-50 text-indigo-600" : "text-slate-600 hover:bg-slate-100"}`}
              >{num}</button>
            );
          })}

          <button
            disabled={page === totalPages}
            onClick={() => fetchData(page + 1)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${page === totalPages ? "text-slate-400 cursor-not-allowed" : "text-slate-600 hover:bg-slate-100"}`}
          >
            Sig →
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════
          MODAL DE DETALLE
      ═══════════════════════════════════════════════════ */}
      {detalle && (
        <ModalDetalleCita cita={detalle} onClose={() => setDetalle(null)} />
      )}
    </div>
  );
};

// ──────────────────────────────────────────────
// Modal de detalle (componente separado)
// ──────────────────────────────────────────────
const ModalDetalleCita = ({ cita, onClose }) => {
  const ei = estadoBadge(cita.estado);
  const ti = tipoBadge(cita.tipo_cita);
  const procedimientos = safeParseJSON(cita.json);
  const total = procedimientos.reduce(
    (acc, s) => acc + Number(s.price || s.precio || 0) * (s.cantidad || 1), 0
  );
  const duracionTotal = procedimientos.reduce(
    (acc, s) => acc + parseInt(s.duracion_minutos || 0) * (s.cantidad || 1), 0
  );

  const t = cita.OdontologiaTratamiento;

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex justify-center items-center p-2 sm:p-4 overflow-hidden"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 sm:p-6 bg-gradient-to-r from-indigo-600 to-indigo-500 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center text-white text-lg font-black">
              {cita.ClientesReceptor?.nombre?.charAt(0) || "?"}
            </div>
            <div>
              <h3 className="text-white font-black text-base leading-tight">
                {cita.ClientesReceptor?.nombre || "Paciente"}
              </h3>
              <p className="text-indigo-200 text-[10px] font-bold uppercase tracking-widest mt-0.5">
                Cita #{cita.id} · {formatFecha(cita.fecha)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body scrollable */}
        <div className="overflow-y-auto custom-scrollbar p-5 sm:p-6 space-y-5">

          {/* Badges estado / tipo */}
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${ei.cls}`}>
              {ei.label}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${ti.cls}`}>
              {ti.label}
            </span>
          </div>

          {/* ── Bloque: Datos generales ── */}
          <section className="bg-slate-50 rounded-xl p-4 border border-slate-100">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">
              📋 Datos Generales
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <DetailRow label="Fecha" value={formatFecha(cita.fecha)} />
              <DetailRow label="Hora inicio" value={formatHora(cita.hora_inicio)} />
              <DetailRow label="Hora fin" value={formatHora(cita.hora_fin)} />
              <DetailRow label="Duración total" value={duracionTotal ? `${duracionTotal} min` : null} accent="text-indigo-600" />
              <DetailRow label="Total servicios" value={`$${total.toFixed(2)}`} accent="text-emerald-600" />
            </div>
          </section>

          {/* ── Bloque: Paciente ── */}
          <section className="bg-slate-50 rounded-xl p-4 border border-slate-100">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">
              👤 Datos del Paciente
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DetailRow label="Nombre" value={cita.ClientesReceptor?.nombre} />
              <DetailRow label="Teléfono" value={cita.ClientesReceptor?.telefono} />
              <DetailRow label="Correo" value={cita.ClientesReceptor?.correo} />
              <DetailRow label="N° Documento" value={cita.ClientesReceptor?.numeroDocumento} />
              <DetailRow label="Dirección" value={cita.ClientesReceptor?.direccionComplemento} />
            </div>
          </section>

          {/* ── Bloque: Tratamiento asociado ── */}
          {t && (
            <section className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">
                🦷 Tratamiento Asociado
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <DetailRow label="ID tratamiento" value={`#${t.id}`} />
                <DetailRow label="Estado" value={t.estado} />
                <DetailRow label="Fecha inicio" value={formatFecha(t.fecha_inicio)} />
                <DetailRow label="Fecha fin est." value={formatFecha(t.fecha_fin)} />
                <DetailRow label="Total estimado" value={`$${parseFloat(t.total_estimado || 0).toFixed(2)}`} accent="text-emerald-600" />
                {t.observaciones && (
                  <div className="col-span-2 sm:col-span-3">
                    <DetailRow label="Observaciones tratamiento" value={t.observaciones} />
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ── Bloque: Procedimientos realizados ── */}
          <section>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">
              🔬 Procedimientos Realizados
            </p>
            {procedimientos.length === 0 ? (
              <p className="text-sm text-slate-400 italic text-center py-6 bg-slate-50 rounded-xl border border-slate-100">
                Sin procedimientos registrados
              </p>
            ) : (
              <div className="space-y-2">
                {procedimientos.map((s, idx) => {
                  const subtotal = Number(s.price || s.precio || 0) * (s.cantidad || 1);
                  return (
                    <div
                      key={s.id || idx}
                      className="flex items-start justify-between gap-3 bg-white border border-slate-100 rounded-xl px-4 py-3 shadow-sm"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center text-xs font-black shrink-0">
                          {s.cantidad || 1}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-800 leading-tight">{s.name}</p>
                          <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mt-0.5">
                            {s.duracion_minutos || 0} min / c/u · SKU: {s.sku || "—"}
                          </p>
                          {s.TipoItem?.name && (
                            <p className="text-[10px] text-slate-400">{s.TipoItem.name}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-emerald-600">${subtotal.toFixed(2)}</p>
                        <p className="text-[10px] text-slate-400">${Number(s.price || s.precio || 0).toFixed(2)} c/u</p>
                      </div>
                    </div>
                  );
                })}

                {/* Total */}
                <div className="flex justify-between items-center bg-indigo-600 text-white rounded-xl px-4 py-3 mt-1">
                  <span className="text-xs font-black uppercase tracking-widest">Total de la Cita</span>
                  <span className="text-lg font-black">${total.toFixed(2)}</span>
                </div>
              </div>
            )}
          </section>

          {/* ── Observaciones ── */}
          {cita.observaciones && (
            <section className="bg-amber-50 border border-amber-100 rounded-xl p-4">
              <p className="text-[10px] font-black text-amber-500 uppercase tracking-widest mb-2">
                📝 Observaciones Clínicas
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">{cita.observaciones}</p>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-5 border-t border-slate-100 bg-slate-50 flex justify-end rounded-b-2xl shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListadoCitasOdontologia;
